/**
 * SubscribeCTA — full-screen call-to-action shown when the current user must
 * subscribe to a peer's exclusive content before performing some action.
 * Originated as a chat-specific component (gating the messaging surface); now
 * reusable: the caller passes in the localized `blurb` and `buttonText`, and a
 * handler invoked when the primary button is tapped.
 *
 * Flutter source of truth (chat-specific origin):
 *  - `_subscribeToExclusiveContentWidget` (`chat_screen.dart:3783-3830`)
 *    composes a centered `Column` of a blurb paragraph and a "Subscribe"
 *    button.
 *  - Tap handler (`chat_screen.dart:3801-3803`): `await _onSubscribeTapped()`
 *    which opens the `SubscribeUserSheet`. Wiring lives in the caller (e.g.
 *    `useChat` for the chat surface) and is passed via `onSubscribePress`.
 *
 * Web-side rendering: the primary button is the project's canonical
 * `ExpauseButton` with `variant={ButtonVariant.primary}`. The Flutter design
 * uses a bordered-only outline button; the web port aligns with the rest of
 * the codebase's primary CTAs for visual consistency across pages (allowed
 * deviation per `presentation.md`).
 *
 * l10n & interpolation: the component is l10n-agnostic — callers resolve
 * their own strings (e.g. `chat.subscribeBlurb` with the `{peerName}`
 * placeholder substituted) and pass them in pre-rendered.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import {
  ButtonVariant,
  ExpauseButton,
} from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface SubscribeCTAProps {
  /**
   * Pre-rendered blurb paragraph. Callers resolve their own l10n string and
   * perform any placeholder substitution (e.g. `{peerName}`) before passing
   * it in. Mirrors Flutter's interpolated string at `chat_screen.dart:122`.
   */
  blurb: string;
  /**
   * Label for the primary button. Callers pass the localized button text
   * (e.g. `chat.subscribeButton`).
   */
  buttonText: string;
  /**
   * Invoked when the Subscribe button is tapped. Mirrors Flutter's
   * `_onSubscribeTapped` (`chat_screen.dart:3801-3803`).
   */
  onSubscribePress: () => void;
  /**
   * Optional override for the Subscribe button's `data-qa-id`. Callers that
   * need a surface-scoped id (so the qa-tester can locate the button on a
   * specific page) pass it here; defaults to the shared `subscribe-cta` id.
   */
  buttonQaId?: string;
}

export const SubscribeCTA: React.FC<SubscribeCTAProps> = ({
  blurb,
  buttonText,
  onSubscribePress,
  buttonQaId = 'subscribe-cta',
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <div
      style={{
        // `AppColors.backgroundColor` — chat_screen.dart:3789.
        backgroundColor: theme.background,
        // Flutter `Padding(EdgeInsets.all(16.0))` around the Column —
        // chat_screen.dart:3791.
        padding: responsive.spacing.lg,
        // Flutter `Column(mainAxisAlignment: MainAxisAlignment.center)` —
        // chat_screen.dart:3793. The widget fills its caller-provided slot;
        // the caller decides the height by mounting it where it needs to.
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          // `AppStyles.subscribeToExclusiveContentStyle` — 16px / regular.
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
          color: theme.text.primary,
          textAlign: 'center',
        }}
      >
        {blurb}
      </span>
      <div
        style={{
          // Flutter `Padding(EdgeInsets.all(32.0))` around the button —
          // chat_screen.dart:3805. Mirrored here as wrapper margins so
          // ExpauseButton's intrinsic sizing (max-width 20%, etc.) is
          // respected without us overriding it via inline styles.
          marginTop: responsive.spacing['3xl'],
          marginBottom: responsive.spacing['3xl'],
          marginLeft: responsive.spacing['3xl'],
          marginRight: responsive.spacing['3xl'],
        }}
      >
        <ExpauseButton
          {...qaAttr({ id: buttonQaId })}
          variant={ButtonVariant.primary}
          onClick={onSubscribePress}
        >
          {buttonText}
        </ExpauseButton>
      </div>
    </div>
  );
};
